import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Wallet, TrendingUp, Coins, Layers, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { formatUSD } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const RANGE_KEYS = ["today","yesterday","7d","30d","month","last_month","custom"];

const PRODUCT_COLORS = ["#0F62FE", "#10B981", "#8B5CF6", "#F59E0B", "#EF4444", "#06B6D4"];

function KpiCard({ icon: Icon, label, value, sub, tone = "blue" }) {
  const tones = {
    blue: { bar: "bg-[#0F62FE]", iconBg: "bg-[#0F62FE]/10 text-[#0F62FE]" },
    slate: { bar: "bg-slate-500", iconBg: "bg-slate-100 text-slate-600" },
    green: { bar: "bg-emerald-500", iconBg: "bg-emerald-50 text-emerald-600" },
    purple: { bar: "bg-violet-500", iconBg: "bg-violet-50 text-violet-600" },
  };
  const cur = tones[tone];
  return (
    <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm p-4 overflow-hidden">
      <div className={`absolute top-0 left-0 w-full h-1 ${cur.bar}`} />
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
          <div className="mt-1.5 text-xl sm:text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
          {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cur.iconBg}`}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

function DateRangePicker({ range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, label }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        data-testid="reports-range-button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm font-semibold"
      >
        <CalendarDays className="w-4 h-4 text-[#0F62FE]" />
        {label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-64 rounded-lg border border-slate-200 bg-white shadow-lg z-40 py-1 text-sm">
            {RANGE_KEYS.map((k) => (
              <button
                key={k}
                data-testid={`reports-range-${k}`}
                onClick={() => { setRange(k); if (k !== "custom") setOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 ${
                  range === k ? "text-[#0F62FE] font-semibold bg-blue-50/40" : "text-slate-700"
                }`}
              >
                {t(`reports.range_${k}`)}
              </button>
            ))}
            {range === "custom" && (
              <div className="border-t border-slate-200 p-3 mt-1 space-y-2">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{t("audit.from")}</label>
                  <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-full h-9 px-2 rounded-md border border-slate-300 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{t("audit.to")}</label>
                  <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-full h-9 px-2 rounded-md border border-slate-300 text-sm" />
                </div>
                <button onClick={() => setOpen(false)} className="w-full h-9 rounded-md bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6]">{t("ui.apply")}</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function ReportsCharts() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "MASTER_ADMIN";
  const [range, setRange] = useState("30d");
  const [customFrom, setCustomFrom] = useState(() => new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [stats, setStats] = useState(null);
  const [byMonth, setByMonth] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [byWeek, setByWeek] = useState([]);
  const [paymentsVsDebit, setPaymentsVsDebit] = useState([]);
  const [byMaster, setByMaster] = useState([]);

  const rangeLabel = useMemo(() => {
    if (range === "custom") return `${customFrom} → ${customTo}`;
    return t(`reports.range_${range}`);
  }, [range, customFrom, customTo, t]);

  useEffect(() => {
    const params = { range };
    if (range === "custom") { params.date_from = customFrom; params.date_to = customTo; }
    api.get("/dashboard/stats", { params }).then((r) => setStats(r.data));
    api.get("/reports/sales-by-month").then((r) => setByMonth(r.data));
    api.get("/reports/sales-by-product", { params: { range: range === "custom" ? "30d" : range } }).then((r) => setByProduct(r.data));
    api.get("/reports/sales-by-week").then((r) => setByWeek(r.data));
    api.get("/reports/payments-vs-debit", { params: { range: range === "custom" ? "30d" : range } }).then((r) => setPaymentsVsDebit(r.data));
    if (isAdmin) {
      api.get("/reports/sales-by-master-agent", { params: { range: range === "custom" ? "30d" : range } })
        .then((r) => setByMaster(r.data || []))
        .catch(() => setByMaster([]));
    }
  }, [range, customFrom, customTo, isAdmin]);

  return (
    <div className="p-4 sm:p-5 space-y-5" data-testid="reports-charts">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("reports.showing_data")} <span className="text-[#0F62FE]">{rangeLabel}</span>
        </div>
        <DateRangePicker
          range={range}
          setRange={setRange}
          customFrom={customFrom}
          setCustomFrom={setCustomFrom}
          customTo={customTo}
          setCustomTo={setCustomTo}
          label={rangeLabel}
        />
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={Wallet} label={t("reports.kpi_available_balance")} value={formatUSD(stats?.wallet_balance_usd ?? 0)} tone="blue" />
        <KpiCard icon={TrendingUp} label={t("reports.kpi_total_sales")} value={formatUSD(stats?.sales_range_usd ?? 0)} sub={`${stats?.successful_topups_range ?? 0} ${t("dashboard.successful")}`} tone="slate" />
        <KpiCard icon={Coins} label={t("reports.kpi_my_commissions")} value={formatUSD(stats?.commission_range_usd ?? 0)} tone="green" />
        <KpiCard icon={Layers} label={t("reports.kpi_total_transactions")} value={stats?.total_transactions_range ?? 0} tone="purple" />
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t("reports.chart_sales_earnings_month")} subtitle={t("reports.last_6_months")}>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={byMonth} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
              <Tooltip formatter={(v) => formatUSD(v)} cursor={{ fill: "rgba(15,98,254,0.05)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sales" fill="#0F62FE" name={t("reports.legend_sales")} radius={[4, 4, 0, 0]} />
              <Bar dataKey="earnings" fill="#10B981" name={t("reports.legend_earnings")} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("reports.chart_sales_by_products")} subtitle={t("reports.top_operators_range")}>
          {byProduct.length === 0 ? (
            <EmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={byProduct}
                  dataKey="sales"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={3}
                  label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                >
                  {byProduct.map((_, i) => (
                    <Cell key={i} fill={PRODUCT_COLORS[i % PRODUCT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatUSD(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t("reports.chart_sales_by_week")} subtitle={t("reports.last_8_weeks")}>
          {byWeek.length === 0 ? (
            <EmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={byWeek} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <Tooltip formatter={(v) => formatUSD(v)} />
                <Line type="monotone" dataKey="sales" stroke="#8B5CF6" strokeWidth={2.5} dot={{ r: 4, fill: "#8B5CF6" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {isAdmin && (
          <ChartCard title={t("reports.chart_sales_by_master")} subtitle={t("reports.top_8_range")} testid="chart-by-master-agent">
            {byMaster.length === 0 ? (
              <EmptyState />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={byMaster}
                    dataKey="sales"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {byMaster.map((_, i) => (
                      <Cell key={i} fill={PRODUCT_COLORS[i % PRODUCT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatUSD(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        )}

        <ChartCard title={t("reports.chart_payments_debits")} subtitle={t("reports.wallet_movements")}>
          {paymentsVsDebit.length === 0 ? (
            <EmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={paymentsVsDebit} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <Tooltip formatter={(v) => formatUSD(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="payments" fill="#10B981" name={t("reports.payments_in")} radius={[4, 4, 0, 0]} />
                <Bar dataKey="debits" fill="#EF4444" name={t("reports.debits_out")} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid={testid}>
      <div className="mb-3">
        <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</div>
        <div className="text-[11px] text-slate-500">{subtitle}</div>
      </div>
      {children}
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="h-[250px] flex items-center justify-center text-sm text-slate-400">
      {t("reports.no_data_range")}
    </div>
  );
}
